/**
 * API functions for Functional Role Configuration
 * 
 * These functions manage the available functional role types that can be assigned
 * to users on projects (Financial Planner, Engineer, etc.).
 */

import { fetcher } from '../utils/fetcher';
import type {
  FunctionalRole,
  FunctionalRoleCreate,
  FunctionalRoleUpdate
} from '../types/team';

const FUNCTIONAL_ROLES_URL = '/api/v1/functional_roles';

/**
 * Get all functional role types
 */
export const getFunctionalRoles = async (activeOnly = false): Promise<FunctionalRole[]> => {
  const url = activeOnly 
    ? `${FUNCTIONAL_ROLES_URL}/read?active_only=true`
    : `${FUNCTIONAL_ROLES_URL}/read`;
  
  return await fetcher(url);
};

/**
 * Get a specific functional role by ID
 */
export const getFunctionalRoleById = async (roleId: string): Promise<FunctionalRole> => {
  return await fetcher(`${FUNCTIONAL_ROLES_URL}/read/${roleId}`);
};

/**
 * Create a new functional role type (Admin only)
 */
export const createFunctionalRole = async (data: FunctionalRoleCreate): Promise<FunctionalRole> => {
  return await fetcher(`${FUNCTIONAL_ROLES_URL}/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
};

/**
 * Update an existing functional role type (Admin only)
 * Note: System-defined roles cannot be modified
 */
export const updateFunctionalRole = async (
  roleId: string,
  data: FunctionalRoleUpdate
): Promise<FunctionalRole> => {
  return await fetcher(`${FUNCTIONAL_ROLES_URL}/update/${roleId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
};

/**
 * Delete a functional role type (Admin only)
 * Note: System-defined roles and roles with active assignments cannot be deleted
 */
export const deleteFunctionalRole = async (roleId: string): Promise<boolean> => {
  return await fetcher(`${FUNCTIONAL_ROLES_URL}/delete/${roleId}`, {
    method: 'DELETE'
  });
};
